import React, { type ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";

import { faSync } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faSparkles } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fetchQuery, graphql } from "relay-runtime";
import { Loader } from "semantic-ui-react";

import { EditorConfig, ProseReact } from "~/lib/prose";
import { getAllMarks } from "~/lib/prose/marks";
import { BulletPoint, HardBreak, HorizontalRule, NumberedListItem, TextNode, Unstyled } from "~/lib/prose/nodes";
import { Header } from "~/lib/prose/nodes/header";
import { Agenda, Indent, Placeholder, Todo } from "~/lib/prose/plugins";
import BlockAnnotation from "~/lib/prose/plugins/blockAnnotation";
import BlockBoundary from "~/lib/prose/plugins/blockBoundary";
import { commitMutationPromise } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import { Button } from "~/ui";
import Checkbox from "~/ui/legacy/Checkbox";
import { Modal, TextArea } from "~/ui/legacyFellowIndex";
import toasty from "~/ui/toast";

import type { AgendaGeneratorPreviewApplyAgendaMutation } from "~/__generated__/AgendaGeneratorPreviewApplyAgendaMutation.graphql";
import type { AgendaGeneratorPreviewHaveAgendaQuery } from "~/__generated__/AgendaGeneratorPreviewHaveAgendaQuery.graphql";
import type {
	AgendaGeneratorPreviewMutation,
	AgendaGeneratorPreviewMutation$data,
} from "~/__generated__/AgendaGeneratorPreviewMutation.graphql";
import type { NoteCard_note$data as Note } from "~/__generated__/NoteCard_note.graphql";
import type { NoteCard_viewer$data as Viewer } from "~/__generated__/NoteCard_viewer.graphql";

import styles from "./AgendaGeneratorPreview.module.css";

interface AgendaGeneratorPreviewProps {
	note: Note;
	viewer: Viewer;
	close(): void;
}

type AgendaType = NonNullable<AgendaGeneratorPreviewMutation$data["generateAgenda"]>["agenda"];

const haveAgendaQuery = graphql`
	query AgendaGeneratorPreviewHaveAgendaQuery($id: ID!) {
		note: node(id: $id) {
			... on Note {
				haveAgenda
			}
		}
	}
`;

const generateAgendaMutation = graphql`
	mutation AgendaGeneratorPreviewMutation($noteId: ID!, $purpose: String) {
		generateAgenda(noteId: $noteId, purpose: $purpose) {
			agenda
		}
	}
`;

const applyGeneratedAgendaMutation = graphql`
	mutation AgendaGeneratorPreviewApplyAgendaMutation(
		$noteId: ID!
		$agenda: JSON!
		$setAsDefaultTemplate: Boolean
		$purpose: String
	) {
		applyGeneratedAgenda(
			noteId: $noteId
			agenda: $agenda
			setAsDefaultTemplate: $setAsDefaultTemplate
			purpose: $purpose
		) {
			ok
		}
	}
`;

export default function AgendaGeneratorPreview({ note, viewer, close }: AgendaGeneratorPreviewProps) {
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<boolean>(false);
	const [agenda, setAgenda] = useState<AgendaType>();
	const [setAsDefaultTemplate, setSetAsDefaultTemplate] = useState<boolean>(false);
	const [showPurposeInput, setShowPurposeInput] = useState<boolean>(false);
	const [purpose, setPurpose] = useState<string>();

	const onClose = useCallback(() => {
		close();
	}, [close]);

	const onChangePurpose = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
		setPurpose(event.target.value);
	}, []);

	const onPreview = useCallback(() => {
		setLoading(true);
		setError(false);
		commitMutationPromise<AgendaGeneratorPreviewMutation>(environment, {
			mutation: generateAgendaMutation,
			variables: {
				noteId: note.id,
				purpose,
			},
			onCompleted: response => {
				const agenda = response.generateAgenda?.agenda;
				setLoading(false);
				setShowPurposeInput(false);
				if (!agenda) {
					toasty.error("Error while generating your agenda. Please try again.");
					return;
				}
				setAgenda(agenda);
				dispatchAnalyticsTrack("Agenda generated", { features: "aiGeneratedAgenda" });
			},
			onError: () => {
				setLoading(false);
				setError(true);
				toasty.error("Error while generating your agenda. Please try again.");
			},
		});
	}, [note, purpose]);

	const onCheckForAgenda = useCallback(async () => {
		setLoading(true);
		setError(false);
		const result = await fetchQuery<AgendaGeneratorPreviewHaveAgendaQuery>(
			environment,
			haveAgendaQuery,
			{ id: note.id },
			{ fetchPolicy: "network-only" },
		).toPromise();
		setLoading(false);
		setError(false);
		dispatchAnalyticsTrack("Opened agenda generator", {
			features: "aiGeneratedAgenda",
			eventHaveAgenda: Boolean(result?.note?.haveAgenda),
		});
		if (!result?.note?.haveAgenda) {
			setShowPurposeInput(true);
			return;
		}
		onPreview();
	}, [onPreview, note.id]);

	const onUseAgenda = useCallback(() => {
		setLoading(true);
		setError(false);
		commitMutationPromise<AgendaGeneratorPreviewApplyAgendaMutation>(environment, {
			mutation: applyGeneratedAgendaMutation,
			variables: {
				noteId: note.id,
				agenda,
				setAsDefaultTemplate,
				purpose,
			},
			onCompleted: response => {
				setLoading(false);
				close();
				if (!response.applyGeneratedAgenda?.ok) {
					toasty.error("Error while applying your agenda. Please try again.");
					return;
				}
				dispatchAnalyticsTrack("Generated agenda used", {
					features: "aiGeneratedAgenda",
					eventWithNoAgenda: Boolean(purpose),
					setAsATemplate: setAsDefaultTemplate,
				});
			},
			onError: () => {
				setLoading(false);
				setError(true);
				toasty.error("Error while applying your agenda. Please try again.");
			},
		});
	}, [note, agenda, setAsDefaultTemplate, purpose, close]);

	const onToggleSetAsDefaultTemplate = useCallback(() => setSetAsDefaultTemplate(value => !value), []);

	const onRegenerate = useCallback(() => {
		onPreview();
		dispatchAnalyticsTrack("Regenerated agenda", { features: "aiGeneratedAgenda" });
	}, [onPreview]);

	const onRetry = useCallback(() => {
		onPreview();
	}, [onPreview]);

	useEffect(() => {
		onCheckForAgenda();
		//	eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const editorConfig: EditorConfig = useMemo(() => {
		const extensions = [
			...getAllMarks({ linkEditable: true, placeholderEditable: false, linkToolBarAllowEmbeds: true }),
			new BlockBoundary(),
			new TextNode(),
			new Indent(),
			new Unstyled(),
			new BulletPoint(),
			new NumberedListItem(),
			new HardBreak(),
			new HorizontalRule(),
			new Header({ viewer }),
			new Placeholder(),
			new Todo({
				note,
				enableCopiedItemPopup: false,
				disableWhenNotEditable: true,
				disabledReadOnly: true,
				viewerId: viewer.user.id,
			}),
			new Agenda({
				note,
				enableCopiedItemPopup: false,
				employeeGuidanceShowManager: false,
				disabledReadOnly: true,
				viewerId: viewer.user.id,
			}),
			new BlockAnnotation(),
		];
		return new EditorConfig({
			extensions: extensions,
			nodes: {
				doc: {
					content: "block+",
				},
			},
		});
		//	eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Modal onClose={onClose} className={styles.modal}>
			<Modal.Header onClose={onClose}>
				<Modal.Title>
					<FontAwesomeIcon icon={faSparkles} /> AI-generated agenda
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{agenda && !loading && !error ? (
					<>
						The agenda below was generated from the event name, description, attendees and duration.{" "}
						<a
							href="https://help.fellow.app/en/articles/7792740-generate-an-ai-meeting-agenda"
							target="_blank"
							rel="noreferrer"
						>
							Learn more.
						</a>
						<div className="editor">
							<ProseReact
								enableReactHost
								className={styles.editor}
								editable={false}
								context={{}}
								initialDoc={agenda}
								config={editorConfig}
								user={viewer.user}
							/>
						</div>
						<Button onClick={onRegenerate} className={styles.regenerate} variant="secondary">
							<FontAwesomeIcon icon={faSync} /> Regenerate
						</Button>
					</>
				) : !loading && showPurposeInput ? (
					<>
						Enter the meeting purpose, and let us do the rest.{" "}
						<a
							href="https://help.fellow.app/en/articles/7792740-generate-an-ai-meeting-agenda"
							target="_blank"
							rel="noreferrer"
						>
							Learn more.
						</a>
						<TextArea
							placeholder="Eg. Discuss project updates, resolve blockers, plan out next steps and assign action items"
							value={purpose}
							onChange={onChangePurpose}
							className={styles.purpose}
						/>
						<small className={styles.tip}>Pro tip: Don&apos;t overthink the grammar, focus on the keywords</small>
					</>
				) : (
					<div className={styles.loading}>
						{loading && <Loader active inline size="medium" className={styles.loader} />}
						{error ? "Meeting agenda could not be generated." : "Pulling event description and generating agenda..."}
						{error && (
							<Button size="slim" variant="primary" onClick={onRetry} disabled={loading}>
								Retry
							</Button>
						)}
					</div>
				)}
			</Modal.Body>
			<Modal.Footer>
				{agenda && !loading && !error && (
					<div className={styles.setDefaultTemplate}>
						<Checkbox checked={setAsDefaultTemplate} onChange={onToggleSetAsDefaultTemplate} />
						<label onClick={onToggleSetAsDefaultTemplate}>Set as default template</label>
					</div>
				)}
				<Button onClick={onClose}>Cancel</Button>
				{showPurposeInput ? (
					<Button variant="primary" onClick={onPreview} disabled={loading || error || !purpose}>
						Generate agenda
					</Button>
				) : (
					<Button variant="primary" onClick={onUseAgenda} disabled={loading || !agenda || error}>
						Use agenda
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
}
